// Copyright (c) Said Borna. All rights reserved.
import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
    type ReactElement,
    type ReactNode,
} from 'react';
import type { UserRole } from '@/types';
import { supabase } from '@/integrations/supabase/client';

/** Impersonation auto-timeout duration in milliseconds (30 minutes). */
const IMPERSONATION_TIMEOUT_MS = 30 * 60 * 1000;

/** Roles that CANNOT be impersonated for security. */
const NON_IMPERSONATABLE_ROLES: ReadonlySet<UserRole> = new Set(['admin']);

/** Impersonation context state. */
interface ImpersonationContextValue {
    /** The impersonated user ID, or null if not impersonating. */
    readonly impersonatedUserId: string | null;
    /** The impersonated user's display name, or null. */
    readonly impersonatedUserName: string | null;
    /** Start impersonating a user. Returns false if target role is admin. */
    readonly startImpersonation: (userId: string, displayName: string, targetRole?: UserRole) => boolean;
    /** Stop impersonating and return to own view. */
    readonly stopImpersonation: () => void;
    /** Whether currently impersonating someone. */
    readonly isImpersonating: boolean;
    /** Minutes remaining before auto-timeout, or null if not impersonating. */
    readonly minutesRemaining: number | null;
}

const ImpersonationContext = createContext<ImpersonationContextValue | undefined>(undefined);

interface ImpersonationProviderProps {
    readonly children: ReactNode;
}

/**
 * Log impersonation events to the audit trail via Supabase.
 * Fire-and-forget — errors are logged but don't break the caller.
 */
async function logImpersonationEvent(
    action: 'impersonation.started' | 'impersonation.ended',
    staffUserId: string,
    targetUserId: string,
    metadata?: Record<string, unknown>,
): Promise<void> {
    try {
        // Get staff's team for audit scoping
        const { data: teamData } = await supabase
            .from('team_members')
            .select('team_id')
            .eq('user_id', staffUserId)
            .limit(1)
            .single();

        await supabase
            .from('activity_logs')
            .insert({
                team_id: teamData?.team_id ?? null,
                user_id: staffUserId,
                action,
                target_type: 'user',
                target_id: targetUserId,
                metadata: metadata ?? {},
            });
    } catch (err: unknown) {
        const message = err instanceof Error ? err.message : 'Unknown error';
        console.error(`[impersonation] Failed to log ${action}:`, message);
    }
}

/**
 * Provides impersonation state for staff viewing client accounts.
 * Includes 30-minute auto-timeout, admin protection, and audit logging.
 */
export function ImpersonationProvider({ children }: ImpersonationProviderProps): ReactElement {
    const [impersonatedUserId, setImpersonatedUserId] = useState<string | null>(null);
    const [impersonatedUserName, setImpersonatedUserName] = useState<string | null>(null);
    const [minutesRemaining, setMinutesRemaining] = useState<number | null>(null);
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const startTimeRef = useRef<number | null>(null);
    const staffUserIdRef = useRef<string | null>(null);

    /** Clear all timers. */
    const clearTimers = useCallback(() => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
        if (countdownRef.current) {
            clearInterval(countdownRef.current);
            countdownRef.current = null;
        }
    }, []);

    const stopImpersonation = useCallback(() => {
        const targetId = impersonatedUserId;
        const staffId = staffUserIdRef.current;

        setImpersonatedUserId(null);
        setImpersonatedUserName(null);
        setMinutesRemaining(null);
        clearTimers();
        startTimeRef.current = null;

        // Audit log (fire-and-forget)
        if (staffId && targetId) {
            logImpersonationEvent('impersonation.ended', staffId, targetId);
        }
    }, [impersonatedUserId, clearTimers]);

    const startImpersonation = useCallback((
        userId: string,
        displayName: string,
        targetRole: UserRole = 'client',
    ): boolean => {
        // Block impersonation of admin roles
        if (NON_IMPERSONATABLE_ROLES.has(targetRole)) {
            console.warn('[impersonation] Cannot impersonate admin role');
            return false;
        }

        // Clear any existing impersonation
        clearTimers();

        setImpersonatedUserId(userId);
        setImpersonatedUserName(displayName);
        startTimeRef.current = Date.now();
        setMinutesRemaining(30);

        // Get current user ID for audit
        supabase.auth.getUser().then(({ data }) => {
            const currentUserId = data.user?.id ?? 'unknown';
            staffUserIdRef.current = currentUserId;

            // Audit log (fire-and-forget)
            logImpersonationEvent('impersonation.started', currentUserId, userId, {
                target_name: displayName,
                target_role: targetRole,
            });
        });

        // Set 30-minute auto-timeout
        timeoutRef.current = setTimeout(() => {
            console.warn('[impersonation] Auto-timeout after 30 minutes');
            stopImpersonation();
        }, IMPERSONATION_TIMEOUT_MS);

        // Update countdown every minute
        countdownRef.current = setInterval(() => {
            if (startTimeRef.current) {
                const elapsed = Date.now() - startTimeRef.current;
                const remaining = Math.max(0, Math.ceil((IMPERSONATION_TIMEOUT_MS - elapsed) / 60000));
                setMinutesRemaining(remaining);
            }
        }, 60000);

        return true;
    }, [clearTimers, stopImpersonation]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            clearTimers();
        };
    }, [clearTimers]);

    const isImpersonating = impersonatedUserId !== null;

    const value = useMemo<ImpersonationContextValue>(
        () => ({
            impersonatedUserId,
            impersonatedUserName,
            startImpersonation,
            stopImpersonation,
            isImpersonating,
            minutesRemaining,
        }),
        [impersonatedUserId, impersonatedUserName, startImpersonation, stopImpersonation, isImpersonating, minutesRemaining],
    );

    return (
        <ImpersonationContext.Provider value={value}>
            {children}
        </ImpersonationContext.Provider>
    );
}

/**
 * Hook to access impersonation state.
 * Must be used within an ImpersonationProvider.
 */
export function useImpersonation(): ImpersonationContextValue {
    const context = useContext(ImpersonationContext);
    if (context === undefined) {
        throw new Error('useImpersonation must be used within an ImpersonationProvider');
    }
    return context;
}

/**
 * Returns the effective user ID — impersonated user if active, otherwise the real user.
 * Use this in all data-fetching hooks to support impersonation.
 */
export function useEffectiveUserId(realUserId: string): string {
    const { impersonatedUserId } = useImpersonation();
    return impersonatedUserId ?? realUserId;
}

/** Staff roles that are allowed to impersonate. */
const IMPERSONATION_ROLES: ReadonlySet<UserRole> = new Set(['admin', 'staff']);

/**
 * Check if a role can impersonate users.
 */
export function canImpersonate(role: UserRole): boolean {
    return IMPERSONATION_ROLES.has(role);
}

/**
 * Check if a target role can be impersonated.
 * Admin roles cannot be impersonated.
 */
// eslint-disable-next-line react-refresh/only-export-components -- utility helpers co-located with context
export function canBeImpersonated(targetRole: UserRole): boolean {
    return !NON_IMPERSONATABLE_ROLES.has(targetRole);
}
